import { asArray, directChildSpaces, sameRefList } from '@repo/front-end-utils';
import type { CompositeSourceAction, OwnCamerasState } from '@repo/ui';
import type {
  FloorPlannerComposite,
  FloorPlannerCompositeRef,
  FloorPlannerLayout,
} from '@repo/frontend-types';

import { decodeRef, encodeRef } from './compositeRefOptions';

/**
 * **What one editor action does to a stored total** (`composites-page-editor`, spec R12–R17).
 *
 * The editor panel holds no copy of the total it shows. It renders the plan, and every action is a
 * function from the stored row to the next stored row, applied by the host to the row as it stands
 * AT WRITE TIME. Pure: no React, no store, no I/O (the stage's no-network guard reads this file).
 *
 * ## One construction site for a stored row
 *
 * `storedShape` rebuilds the record from its four fields and drops every other key — `generated`
 * included. That is right for a real change (Rob, 2026-09-30: an edited generated total becomes an
 * ordinary hand-made one) and is why a change that alters nothing must not be written at all: the
 * host checks `sameDefinition` first, so opening and closing a generated total keeps it generated.
 */

/** A total's four defining fields, with its references decoded. */
interface SubmittedComposite {
  target: FloorPlannerCompositeRef;
  inputs: FloorPlannerCompositeRef[];
  includesOwnObservers: boolean;
  excluded: boolean;
}

/**
 * The record the plan stores.
 *
 * ⚠ **Built from the fields rather than spread over the existing measure, and that is the fix for a
 * real defect.** The edit path used to be `{...existing, ...(flag ? {flag: true} : {})}`, which can
 * only ever turn a flag ON: un-ticking one contributed no key, the old `true` survived the spread,
 * and the stage said "Saved" while the plan kept the opposite. A flag is written only when true,
 * which keeps a plan document carrying it only where it means something — the same rule
 * `matchCompositesToPlan` follows when it places one.
 */
export const storedShape = (refid: string, value: SubmittedComposite): FloorPlannerComposite => ({
  refid,
  target: value.target,
  inputs: value.inputs,
  ...(value.includesOwnObservers ? { includesOwnObservers: true } : {}),
  ...(value.excluded ? { excluded: true } : {}),
});

/**
 * Does `value` say exactly what the stored total already says? Target and members by their
 * canonical encoding (in order — order is part of a member list), and the two flags read as
 * booleans, because absent and `false` mean the same thing on the plan.
 */
export const sameDefinition = (stored: FloorPlannerComposite, value: SubmittedComposite): boolean =>
  encodeRef(stored.target) === encodeRef(value.target) &&
  sameRefList(asArray(stored.inputs), value.inputs) &&
  (stored.includesOwnObservers === true) === (value.includesOwnObservers === true) &&
  (stored.excluded === true) === (value.excluded === true);

/** A stored row's four fields. */
export const fieldsOf = (row: FloorPlannerComposite): SubmittedComposite => ({
  target: row.target,
  inputs: asArray(row.inputs),
  includesOwnObservers: row.includesOwnObservers === true,
  excluded: row.excluded === true,
});

/** Two stored rows that define the same total — the editor's "has this changed under me?" test. */
export const sameStored = (a: FloorPlannerComposite, b: FloorPlannerComposite): boolean =>
  sameDefinition(a, fieldsOf(b));

/** The same total, with one more source on the end. */
export const withInputAdded = (
  row: FloorPlannerComposite,
  input: FloorPlannerCompositeRef,
): FloorPlannerComposite =>
  storedShape(row.refid, { ...fieldsOf(row), inputs: [...asArray(row.inputs), input] });

/** The same total without the source at `at` — by position, so a source listed twice loses one. */
export const withInputRemoved = (row: FloorPlannerComposite, at: number): FloorPlannerComposite =>
  storedShape(row.refid, {
    ...fieldsOf(row),
    inputs: asArray(row.inputs).filter((_, i) => i !== at),
  });

/** The same total, counting its target's own cameras or not. */
export const withOwnCameras = (row: FloorPlannerComposite, on: boolean): FloorPlannerComposite =>
  storedShape(row.refid, { ...fieldsOf(row), includesOwnObservers: on });

/** The same total, excluded from processing or not. */
export const withExcluded = (row: FloorPlannerComposite, on: boolean): FloorPlannerComposite =>
  storedShape(row.refid, { ...fieldsOf(row), excluded: on });

/** The same sources and flags, totalling something else. */
export const withTarget = (
  row: FloorPlannerComposite,
  target: FloorPlannerCompositeRef,
): FloorPlannerComposite => storedShape(row.refid, { ...fieldsOf(row), target });

/**
 * A new total for `target`, filled in from the plan (spec R13; Routes: *"the form fills in
 * suggested sources (its child spaces, its own cameras if it has any), all removable"*).
 *
 * - **Inputs:** its direct child spaces, in the plan's order — the same list the page's generated
 *   totals use (`directChildSpaces`). Equipment is never prefilled; it stays addable by hand.
 * - **Its own cameras:** counted when at least one observer watches it. At two or more the rules
 *   REQUIRE it, and the editor shows the row locked.
 */
export const prefillFor = (
  layout: FloorPlannerLayout | null | undefined,
  refid: string,
  target: FloorPlannerCompositeRef,
  observerCounts: ReadonlyMap<string, number>,
): FloorPlannerComposite => {
  const key = encodeRef(target);
  const children = layout ? (directChildSpaces(layout).childSpaces.get(key) ?? []) : [];
  return storedShape(refid, {
    target,
    inputs: children.filter(child => encodeRef(child) !== key),
    includesOwnObservers: (observerCounts.get(key) ?? 0) >= 1,
    excluded: false,
  });
};

/**
 * How the editor's *Its own cameras* row reads (spec R9), from how many live observers watch the
 * target and whether the total counts them.
 *
 * - `locked` — two or more, counted: the rules require it, so there is nothing to change.
 * - `required` — two or more, NOT counted: a stored total the rules refuse. Opening it never
 *   changes it; the row offers *Add*.
 * - `removable` — counted, one or none: the operator's choice.
 * - `available` — one, not counted: offered.
 * - `none` — none, not counted: said, nothing offered.
 */
export const ownCamerasState = (watching: number, counted: boolean): OwnCamerasState => {
  if (watching >= 2) return counted ? 'locked' : 'required';
  if (counted) return 'removable';
  return watching === 1 ? 'available' : 'none';
};

/** The prefix the stage keys an automatic row with: `automatic:{encoded target}`. */
export const AUTOMATIC_ROW_PREFIX = 'automatic:';

/** An automatic row's refid — the one builder, read back by {@link rowTargetKey}. */
export const automaticRowRefid = (encodedTarget: string): string =>
  `${AUTOMATIC_ROW_PREFIX}${encodedTarget}`;

/** The encoded target a list row totals — an automatic row carries it in its own refid. */
export const rowTargetKey = (
  refid: string,
  written: ReadonlyArray<FloorPlannerComposite>,
): string | null => {
  if (refid.indexOf(AUTOMATIC_ROW_PREFIX) === 0) {
    const key = refid.slice(AUTOMATIC_ROW_PREFIX.length);
    return decodeRef(key) ? key : null;
  }
  const row = written.find(c => c.refid === refid);
  const key = row ? encodeRef(row.target) : '';
  return key === '' ? null : key;
};

/**
 * Which list rows offer *Add as source*, which read *Added*, and which read *In total* — matched
 * on the row's ENCODED TARGET, because a source is a reference to a thing on the plan, not to a
 * row (an automatic row has no stored total at all).
 *
 * Left out: the total being edited, a row whose target the plan no longer holds, every row
 * while an Add session has no target yet, and — offered no *Add as source* — a row whose target
 * would make the edited total add up itself (`loops`, the *Add source* picker's own rule, so the
 * two ways of adding a source offer the same things; `composites-page-picker`, spec R11). A row
 * already in the total still reads *In total*: that is a fact about the total, not an offer.
 * `justAdded` is the row refid that reads *Added* for a moment after it was added.
 */
export const sourceActionsFor = ({
  rows,
  written,
  session,
  justAdded,
  loops = () => false,
}: {
  rows: ReadonlyArray<{ refid: string; target: { unresolved: boolean } }>;
  written: ReadonlyArray<FloorPlannerComposite>;
  /** The total being edited, as it stands; `null` while there is none. */
  session: FloorPlannerComposite | null;
  justAdded: string | null;
  /** Would adding this encoded target make the edited total loop? (`wouldLoop`.) */
  loops?: (key: string) => boolean;
}): Map<string, CompositeSourceAction> => {
  const out = new Map<string, CompositeSourceAction>();
  if (!session) return out;
  const own = encodeRef(session.target);
  const inputs = new Set(asArray(session.inputs).map(encodeRef));
  for (const row of rows) {
    if (row.refid === session.refid || row.target.unresolved) continue;
    const key = rowTargetKey(row.refid, written);
    if (key === null || key === own) continue;
    if (inputs.has(key)) out.set(row.refid, row.refid === justAdded ? 'added' : 'in-total');
    else if (!loops(key)) out.set(row.refid, 'add');
  }
  return out;
};
